import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { AUTH_EXPIRED_EVENT } from "../lib/api.js";
import { API_BASE } from "../lib/api.js";
import { authApi } from "./api.js";
import { type AuthPublicConfig, fetchAuthConfig } from "./auth-config.js";
import { msalGetToken } from "./msal.js";
import { type StoredAuth, clearStoredAuth, readStoredAuth, writeStoredAuth } from "./storage.js";

interface AuthState {
  user: StoredAuth["user"] | null;
  /** Aktive Auth-Strategie/-Modus vom Backend (null bis /auth/config geladen ist). */
  config: AuthPublicConfig | null;
  /** true waehrend des initialen /me-Checks und waehrend login() laeuft. */
  loading: boolean;
  error: string | null;
  /** Demo-Login per User-ID. */
  login: (userId: string) => Promise<void>;
  /** Entra-Login: mode=web -> Server-Redirect, mode=spa -> MSAL-Popup. */
  loginEntra: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Liest ein Token (oder einen Fehler) aus dem URL-Fragment, das der Entra-web-Callback
 * anhaengt (#auth_token=… / #auth_error=…), und entfernt es sofort aus der Adresszeile.
 * Wird genau einmal beim Modul-Import ausgewertet (vor dem ersten Render).
 */
function consumeAuthFragment(): { token?: string; error?: string } {
  if (typeof window === "undefined" || !window.location.hash) return {};
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("auth_token");
  const error = params.get("auth_error");
  if (!token && !error) return {};
  // Fragment aus URL strippen, damit ein Reload den Token nicht erneut verarbeitet.
  history.replaceState(null, "", window.location.pathname + window.location.search);
  const out: { token?: string; error?: string } = {};
  if (token) out.token = token;
  if (error) out.error = error;
  return out;
}

const fragment = consumeAuthFragment();

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const initialStored = readStoredAuth();
  const [user, setUser] = useState<StoredAuth["user"] | null>(
    fragment.token ? null : (initialStored?.user ?? null),
  );
  const [config, setConfig] = useState<AuthPublicConfig | null>(null);
  // Loading solange ein Bootstrap aussteht: Fragment-Token einloesen ODER Stored-Token gegen /me pruefen.
  const [loading, setLoading] = useState<boolean>(fragment.token != null || initialStored !== null);
  const [error, setError] = useState<string | null>(fragment.error ? "entra" : null);

  // Strategie/Modus laden (steuert, welchen Login-Screen wir zeigen).
  useEffect(() => {
    let cancelled = false;
    fetchAuthConfig()
      .then((cfg) => {
        if (!cancelled) setConfig(cfg);
      })
      .catch(() => {
        // Config-Fehler -> Default bleibt null; LoginScreen zeigt dann den Demo-Fallback nicht
        // faelschlich an, sondern eine Lade-/Fehlermeldung.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bootstrap: entweder ein frisches Entra-Token aus dem Fragment einloesen, oder einen
  // gespeicherten Token gegen /me validieren (Server ist Wahrheit, localStorage nur Cache).
  // biome-ignore lint/correctness/useExhaustiveDependencies: einmaliger Mount-Bootstrap; fragment/initialStored sind absichtliche Captures
  useEffect(() => {
    let cancelled = false;

    if (fragment.token) {
      const token = fragment.token;
      authApi
        .meWithToken(token)
        .then((res) => {
          if (cancelled) return;
          writeStoredAuth({ token, user: res.user });
          setUser(res.user);
        })
        .catch(() => {
          if (!cancelled) setError("entra");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    if (initialStored === null) return;
    authApi
      .me()
      .then((res) => {
        if (cancelled) return;
        setUser(res.user);
        writeStoredAuth({ token: initialStored.token, user: res.user });
      })
      .catch(() => {
        // 401 -> AUTH_EXPIRED_EVENT-Handler setzt user=null. Andere Fehler ignorieren wir;
        // der naechste API-Call versucht es erneut.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = (): void => setUser(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, []);

  const login = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { token, user: loggedIn } = await authApi.demoLogin(userId);
      writeStoredAuth({ token, user: loggedIn });
      setUser(loggedIn);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginEntra = useCallback(async () => {
    if (!config?.entra) return;
    setError(null);
    if (config.entra.mode === "web") {
      // Serverseitiger Code-Flow: Backend redirected zu Microsoft und liefert das Token
      // anschliessend im URL-Fragment zurueck (siehe consumeAuthFragment).
      window.location.href = `${API_BASE}/auth/login`;
      return;
    }
    // mode=spa: Token per MSAL-Popup holen und wie ein Demo-Token verarbeiten.
    setLoading(true);
    try {
      const token = await msalGetToken({
        clientId: config.entra.clientId,
        tenantId: config.entra.tenantId,
        scope: config.entra.scope,
      });
      const res = await authApi.meWithToken(token);
      writeStoredAuth({ token, user: res.user });
      setUser(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  }, [config]);

  const logout = useCallback(() => {
    clearStoredAuth();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, config, loading, error, login, loginEntra, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
